import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useScreenVisibility } from '../../context/ScreenVisibilityContext';

interface SidebarItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  children?: SidebarItem[];
}

const sidebarItems: SidebarItem[] = [
  {
    id: 'start',
    label: 'Start',
    path: '/start'
  },
  {
    id: 'user-administration',
    label: 'User Administration',
    path: '/user-administration',
    children: [
      {
        id: 'screen-visibility',
        label: 'Screen Visibility Control',
        path: '/framework/screen-visibility'
      },
      {
        id: 'user-management',
        label: 'User Management',
        path: '/framework/user-management'
      }
    ]
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    children: [
      {
        id: 'setup',
        label: 'Assessment Framework Setup',
        path: '/framework',
        children: [
          {
            id: 'dimensions',
            label: 'Dimensions',
            path: '/framework/dimensions'
          },
          {
            id: 'components',
            label: 'Components',
            path: '/framework/components'
          },
          {
            id: 'attributes',
            label: 'Data Attributes and Weightage',
            path: '/framework/attributes'
          },
          {
            id: 'questionnaire',
            label: 'Questionnaire Setup',
            path: '/framework/questionnaire'
          },
          {
            id: 'questions',
            label: 'Questions Management',
            path: '/framework/questions'
          },
          {
            id: 'framework-definition',
            label: 'Framework Definition',
            path: '/framework/definition'
          },
          {
            id: 'framework-builder',
            label: 'Framework Builder',
            path: '/framework/builder'
          },
          {
            id: 'scoring-model',
            label: 'Disposition Matrix',
            path: '/framework/scoring-model'
          }
        ]
      }
    ]
  },
  {
    id: 'master-data',
    label: 'Master Data',
    path: '/master-data',
    children: [
      {
        id: 'application-master-data',
        label: 'Application Master Data',
        path: '/customers/applications'
      },
      {
        id: 'costing',
        label: 'Costing',
        path: '/costing',
        children: [
          {
            id: 'current-run-rate',
            label: 'Current Run Rate',
            path: '/customers/costing/current-run-rate'
          },
        ]
      }
    ]
  },
  {
    id: 'apr-rationalization-models',
    label: 'APR Rationalization Models',
    path: '/apr-rationalization-models',
    children: [
      {
        id: 'apr-model-definition',
        label: 'APR Model Definition',
        path: '/apr-model'
      },
      {
        id: 'apr-model-execution',
        label: 'APR Model Execution',
        path: '/execution'
      }
    ]
  },
  {
    id: 'application-evaluation',
    label: 'Application Evaluation',
    path: '/evaluation',
    children: [
      {
        id: 'initial-4r-disposition',
        label: 'Initial Disposition',
        path: '/evaluation/initial-4r-disposition',
        children: [
          {
            id: 'application-portfolio-qa',
            label: 'Application Portfolio Q&A',
            path: '/evaluation/qa'
          },
          {
            id: 'application-portfolio-scoring',
            label: 'Initial Application Portfolio Scoring',
            path: '/evaluation/scoring'
          },
          {
            id: 'data-export-reports',
            label: 'Data Export & Reports',
            path: '/evaluation/reports'
          }
        ]
      },
      {
        id: 'final-4r-disposition',
        label: 'Final Disposition',
        path: '/evaluation/final-disposition'
      }
    ]
  },
  {
    id: 'integration-framework',
    label: 'Roadmap & Planning',
    path: '/integration-framework',
    children: [
      {
        id: 'application-master-data-if',
        label: 'Applications for Roadmap Analysis',
        path: '/integration-framework/application-master-data'
      },
      {
        id: 'roadmap-implementation-planning',
        label: 'Roadmap/Map & Grouping',
        path: '/integration-framework/roadmap-implementation-planning'
      },
      {
        id: 'application-dependencies',
        label: 'Application Dependencies',
        path: '/integration-framework/application-dependencies'
      },
      {
        id: 'phase-effort-planning',
        label: 'Effort Planning',
        path: '/integration-framework/phase-effort-planning'
      },
      {
        id: 'resource-management',
        label: 'Resource Management',
        path: '/integration-framework/resource-management',
        children: [
          {
            id: 'resource-skillsets',
            label: 'Resource Skillsets',
            path: '/integration-framework/resource-skillsets'
          },
          {
            id: 'resource-capacity',
            label: 'Resource Capacity',
            path: '/integration-framework/resource-capacity'
          },
          {
            id: 'resource-allocation',
            label: 'Resource Allocation',
            path: '/integration-framework/resource-allocation'
          }
        ]
      },
      {
        id: 'roadmap-schedule',
        label: 'Roadmap Schedule',
        path: '/integration-framework/roadmap-schedule'
      },
      {
        id: 'role-utilization',
        label: 'Role Utilization',
        path: '/integration-framework/role-utilization'
      }
    ]
  },
  {
    id: 'disposition-control',
    label: 'Disposition Control',
    path: '/integration-framework/disposition-control'
  },
  {
    id: 'disposition-cost-analysis',
    label: 'Disposition Cost Analysis',
    path: '/integration-framework/disposition-cost-analysis'
  },
  {
    id: 'benefit-realization',
    label: 'Benefit Realization',
    path: '/benefit-realization'
  },
  {
    id: 'high-level-roadmap',
    label: 'High Level RoadMap',
    path: '/roadmap',
    children: [
      {
        id: 'implementation-roadmap',
        label: 'Implementation RoadMap',
        path: '/roadmap/implementation'
      }
    ]
  },
  {
    id: 'dashboard',
    label: 'Executive Dashboard',
    path: '/dashboard'
  },
  {
    id: 'user-guide',
    label: 'User Guide',
    path: '/user-guide'
  },
  {
    id: 'help-support',
    label: 'Help & Support',
    path: '/help-support'
  }
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { currentUser, hasPermission, logout } = useAuth();
  const { isRouteBlocked, isSoftura } = useScreenVisibility();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  // Find all parent items of the active path
  const findActiveParents = (items: SidebarItem[], targetPath: string, parents: string[] = []): string[] => {
    for (const item of items) {
      const currentParents = [...parents, item.id];
      
      // Check if this item or any of its children match the target path
      if (item.path === targetPath) {
        return currentParents;
      }
      
      // Check children recursively
      if (item.children) {
        const childResult = findActiveParents(item.children, targetPath, currentParents);
        if (childResult.length > 0) {
          return childResult;
        }
      }
    }
    return [];
  };

  // Update expanded items when location changes or user role changes
  React.useEffect(() => {
    // For Application Owner, always expand the evaluation menu to show Q&A
    if (currentUser?.role === 'Application Owner') {
      const expandedForOwner = ['application-evaluation', 'initial-4r-disposition'];
      setExpandedItems(expandedForOwner);
    } else {
      // For Administrator and User, start with all menus collapsed
      setExpandedItems([]);
    }
    
    // Scroll sidebar to top when user logs in
    const sidebarNav = document.querySelector('.sidebar-nav');
    if (sidebarNav) {
      sidebarNav.scrollTop = 0;
    }
  }, [currentUser?.role]);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const shouldShowItem = (item: SidebarItem): boolean => {
    // Screen Visibility Control is only visible to @softura.com administrators
    if (item.id === 'screen-visibility') {
      return hasPermission('canManageUsers') && isSoftura;
    }

    // Application Owner access control - only show Q&A related items
    if (currentUser?.role === 'Application Owner') {
      const allowedItems = [
        'application-evaluation',
        'initial-4r-disposition',
        'application-portfolio-qa'
      ];
      return allowedItems.includes(item.id);
    }

    // Screen visibility check (supersedes role-based rules)
    // For leaf items (no children), check if the route is blocked
    if (!item.children || item.children.length === 0) {
      if (isRouteBlocked(item.path)) {
        return false;
      }
    }

    // For parent items with children, check if ALL visible children are blocked
    // If so, hide the parent too
    if (item.children && item.children.length > 0) {
      const hasAnyVisibleChild = item.children.some(child => shouldShowItem(child));
      if (!hasAnyVisibleChild) {
        return false;
      }
    }

    // User Administration menu is only visible to Administrators
    if (item.id === 'user-administration') {
      return hasPermission('canManageUsers');
    }

    // User Management is only visible to Administrators
    if (item.id === 'user-management') {
      return hasPermission('canManageUsers');
    }

    // Settings menu is only visible to Administrators
    if (item.id === 'settings') {
      return hasPermission('canManageSettings');
    }

    // Dashboard is visible to everyone except Application Owner
    if (item.id === 'dashboard') {
      return hasPermission('canViewDashboard');
    }

    // All other menus are visible if user can view all menus
    return hasPermission('canViewAllMenus');
  };

  const renderSidebarItem = (item: SidebarItem, level: number = 0) => {
    // Check if item should be shown based on user role
    if (!shouldShowItem(item)) {
      return null;
    }
    
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const active = isActive(item.path);
    
    // Filter children based on permissions
    const visibleChildren = hasChildren ? item.children!.filter(shouldShowItem) : [];

    return (
      <div key={item.id} className={`sidebar-item level-${level}`}>
        <div className={`sidebar-item-content ${active ? 'active' : ''}`}>
          <Link
            to={item.path}
            className="sidebar-link"
            style={{ paddingLeft: `${level * 20 + 12}px` }}
          >
            <span className="sidebar-label">{item.label}</span>
          </Link>
          {visibleChildren.length > 0 && (
            <button
              className="sidebar-toggle"
              onClick={() => toggleExpanded(item.id)}
              aria-label={`Toggle ${item.label} submenu`}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
        </div>
        {visibleChildren.length > 0 && isExpanded && (
          <div className="sidebar-children">
            {visibleChildren.map(child => renderSidebarItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sidebar">
      <nav className="sidebar-nav">
        {sidebarItems.filter(shouldShowItem).map(item => renderSidebarItem(item))}
      </nav>
    </div>
  );
};

export default Sidebar;